/**
 * PublicWorkshopReport - Dynamic public workshop report.
 * Route: /raportti?instance={instanceId}
 *
 * Thin orchestrator that fetches all data and renders section components.
 * Works for any workshop instance — no hardcoded content.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { Sparkles, X, Printer } from 'lucide-react';
import ModalWrapper from '../components/ModalWrapper';
import WorkshopAction from '../components/Report/WorkshopAction';
import { API_ENDPOINTS } from '../config/api';
import MainViewHeader from '../components/MainViewHeader';
import useContentRefresh from '../hooks/useContentRefresh';

// Report sections
import ReportHeader from '../components/Report/ReportHeader';
import ReportNav from '../components/Report/ReportNav';
import ReportStats from '../components/Report/ReportStats';
import ReportTranscript from '../components/Report/ReportTranscript';
import ReportScenarios from '../components/Report/ReportScenarios';
import ReportPivotPoints from '../components/Report/ReportPivotPoints';
import ReportLenses from '../components/Report/ReportLenses';
import ReportSolutions from '../components/Report/ReportSolutions';
import ReportHybridSolutions from '../components/Report/ReportHybridSolutions';
import ReportOrgActions from '../components/Report/ReportOrgActions';
import SolutionAnalysis, { getCompositeScore, isTagBasedEvaluation } from '../components/Report/SolutionAnalysis';
import { getEvaluationTagLabels } from '@kuutio/shared/evaluationTags.js';
import { dateLocale, hasStaticUi } from '@kuutio/shared/reportLanguages.js';
import SolutionImplementationPlan from '../components/Report/SolutionImplementationPlan';
import SolutionBlockerAnswers from '../components/Report/SolutionBlockerAnswers';
import SolutionsFirstSteps from '../components/Report/SolutionsFirstSteps';
import EditableText from '../components/Report/EditableText';
import EditorDrawerTab from '../components/Report/EditorDrawerTab';
import ReportFutureStory from '../components/Report/ReportFutureStory';
import ReportWhatIf from '../components/Report/ReportWhatIf';
import ParticipantWhatIfCards from '../components/Report/ParticipantWhatIfCards';
import ScenarioChoiceCards from '../components/Report/ScenarioChoiceCards';
import ReportParticipants from '../components/Report/ReportParticipants';
import { ReportAuthProvider, useReportAuth } from '../components/Report/ReportAuthContext';
import { useReportEdits } from '../components/Report/ReportEditsContext';
import { useReportMeta } from '../components/Report/useReportMeta';
import { useTranslation } from '../hooks/useTranslation.jsx';
import { lensDisplayName } from '../utils/lensDisplay';
import { normalizeImageUrl } from '../components/Report/imageUrl';
import SectionGate from '../components/Report/SectionGate';
import ReportChainSections from '../components/Report/ReportChainSections';
import useChainReportData, { CHAIN_NAV_KEYS } from '../components/Report/useChainReportData';
import ReportForesightSections, { useForesightReportData, FORESIGHT_NAV_KEYS } from '../components/Report/ReportForesightSections';
import ReportFutureSections, { useFutureReportData, FUTURE_NAV_KEYS, FUTURE_SECTIONS } from '../components/Report/ReportFutureSections';
import TopBarReportActions from '../components/Report/TopBarReportActions';
import { resolveSectionAccess, ACCESS_HIDDEN, ACCESS_AUTHED } from '../components/Report/sectionAccess';
import { fetchJson } from '../components/Report/reportFetch';
import ReportSources from '../components/Report/ReportSources';
import ReportImageSlot from '../components/Report/ReportImageSlot';
import TulevaisuuskuvatGroup from '../components/Report/TulevaisuuskuvatGroup';
import AtmosphereGroup from '../components/Report/AtmosphereGroup';

// Shared styles
import styled from '@emotion/styled';
import {
  Container, Main, Divider, Footer, Section, SectionTitle, Paragraph,
  LoadingContainer, Spinner, LoadingText, ErrorContainer,
} from '../components/Report/reportStyles';

const PrintOnlySection = styled(Section)`
  display: none;
  @media print { display: block; }
`;

// Top bar rendered inside ReportAuthProvider so it can react to PIN state.
// When the report PIN is required and the viewer hasn't signed in, we hand
// MainViewHeader a `titleOverride` — that drops the chevron and disables
// the section dropdown, so non-authed visitors can't jump into other Kuutio
// views from here.
//
// Two layouts depending on Sitra-Entra sign-in:
//   - Sitra session:   Muokkaa + Tulosta collapse into the avatar's
//                      UserDropdown. No inline buttons, no hamburger
//                      clutter — the team uses one menu for everything.
//   - No Sitra session: the public viewer flow. Inline Kirjaudu pill
//                      stays; on mobile the hamburger collapses it.
function ReportTopBar({ instanceTitle, onPrint }) {
  const { pinRequired, isAuthed } = useReportAuth();
  const { t } = useTranslation();
  const locked = pinRequired && !isAuthed;

  // Muokkaa + Tulosta live inline next to the avatar on desktop and
  // collapse into the hamburger panel on mobile — same component for
  // both Sitra and public viewers. TopBarReportActions renders the
  // Kirjaudu pill only when there's a PIN gate AND the viewer isn't
  // already authed (Sitra session counts as authed → no pill).
  return (
    <MainViewHeader
      activeView="report"
      instanceTitle={instanceTitle}
      hideStatusDot
      titleOverride={locked ? t('report.aiSummaryTitle') : undefined}
    >
      <TopBarReportActions onPrint={onPrint} />
    </MainViewHeader>
  );
}

/**
 * The facilitator's turns are stage directions — "the clock is on the screen",
 * "microphone, one minute each" — and they surface as pull-quotes attributed to
 * a participant, which reads as though the room said something it did not.
 *
 * Filtered where the transcript enters the report rather than at each render
 * site: the highlights logic already carried its own copy of this check and the
 * phase sections did not, which is exactly how the logistics ended up quoted.
 * The database keeps every turn; only the report drops them.
 *
 * Labels are written by the transcribe pipeline as "SPEAKER_01 (Fasilitaattori)",
 * "Fasilitaattori 2", or a bare "Fasilitaattori" depending on the workshop.
 *
 * The label alone is not enough. Some older workshops (K2's "Entä jos -nostot",
 * all four segments) have participant turns merged INTO the facilitator's
 * segment, still carrying their "[75.7s] SPEAKER_05:" headers in the body.
 * Dropping those on the label would delete what participants actually said and
 * empty the section. A segment that carries another speaker's turn is kept.
 */
const FACILITATOR = /fasilitaattori|facilitator/i;
const EMBEDDED_SPEAKER = /\[\d+(?:\.\d+)?s\]\s*SPEAKER_/i;
const dropFacilitator = (segments) =>
  segments.filter(s => !FACILITATOR.test(s?.speaker_label || s?.label || '')
    || EMBEDDED_SPEAKER.test(s?.text || ''));

export default function PublicWorkshopReport() {
  const [searchParams] = useSearchParams();
  const { slug } = useParams();
  // Whether a sign-in opens the report is the server's answer
  // (ReportAuthProvider, #1475), not ReportEditsContext's `me` alone.
  // get() reads from the ReportEdits overlay so meta tags can use edited
  // title / subtitle / vaikutuskartta image without a duplicate fetch.
  const {
    canEdit,
    get,
    getTranslated,
    isTranslatedView,
    instanceLanguage,
    setViewLanguage,
  } = useReportEdits();
  const { t, language, setLanguage } = useTranslation();
  const langFromUrl = searchParams.get('lang');
  // Two entry shapes:
  //   - Legacy: /raportti?kid=<uuid>       → instanceId is in the query
  //   - Pretty: /raportti/K5-mahdollisuuksien-akatemia → slug resolves to id
  // Slug wins when present. The resolver hits /api/instances/by-slug/<slug>
  // and fills resolvedInstanceId on success; downstream useEffects already
  // depend on instanceId, so they re-run as soon as it lands.
  const queryInstanceId = searchParams.get('kid') || searchParams.get('instance') || searchParams.get('instanceId');
  const [resolvedInstanceId, setResolvedInstanceId] = useState(slug ? null : queryInstanceId);
  const [slugError, setSlugError] = useState(null);
  useEffect(() => {
    if (!slug) { setResolvedInstanceId(queryInstanceId); return; }
    let cancelled = false;
    setSlugError(null);
    fetch(API_ENDPOINTS.INSTANCE_BY_SLUG(slug))
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(inst => { if (!cancelled) setResolvedInstanceId(inst.id); })
      .catch(e => { if (!cancelled) setSlugError({ slug, message: e.message }); });
    return () => { cancelled = true; };
  }, [slug, queryInstanceId]);
  const instanceId = resolvedInstanceId;
  const isPrintMode = searchParams.get('print') === 'true';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [printIncludeSolutions, setPrintIncludeSolutions] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);

  // Data state
  const [instanceInfo, setInstanceInfo] = useState(null);

  // SectionVisibilityPanel broadcasts after a successful PATCH so we can
  // merge the new access map into instanceInfo without a full refetch.
  // Without this, flipping a section to Hidden/Authed wouldn't take effect
  // until the viewer reloads — admins expect their toggle to land instantly.
  useEffect(() => {
    const handler = (e) => {
      if (!e.detail) return;
      if (e.detail.instanceId && instanceId && e.detail.instanceId !== instanceId) return;
      setInstanceInfo(prev => prev ? ({
        ...prev,
        settings: {
          ...(prev.settings || {}),
          reportSectionAccess: e.detail.reportSectionAccess,
        },
      }) : prev);
    };
    window.addEventListener('kuutio:section-access-changed', handler);
    return () => window.removeEventListener('kuutio:section-access-changed', handler);
  }, [instanceId]);

  // Viewer's "locked-out" state (pinRequired && !any-auth) computed
  // synchronously from the signals already in scope.
  //
  // Earlier this was driven by a window event from ReportAuthProvider,
  // but viewerLocked stayed `false` for one render after instanceInfo
  // loaded — long enough for Suojattu-gated content (Miten aloitamme,
  // etc.) to render to incognito viewers and stay rendered because the
  // event listener's setState batched in a way that didn't always
  // re-trigger the gated subtree. Computing the lock state directly
  // each render closes that race.
  //
  // authTick bumps when the auth provider broadcasts state changes,
  // forcing a re-read of localStorage (the only signal that changes
  // without going through React state).
  const [, setAuthTick] = useState(0);
  useEffect(() => {
    const handler = () => setAuthTick(t => t + 1);
    window.addEventListener('kuutio:report-chat-locked', handler);
    return () => window.removeEventListener('kuutio:report-chat-locked', handler);
  }, []);
  const pinRequired = !!instanceInfo?.settings?.hasReportPin;
  // The provider's mirror of the server's answer: a report-PIN session, or a
  // sign-in that opens this report (#1475).
  const pinAuthed = (() => {
    if (!instanceId || !pinRequired) return false;
    try { return localStorage.getItem(`kuutio-report-auth-${instanceId}`) === '1'; } catch { return false; }
  })();
  const viewerLocked = pinRequired && !pinAuthed;

  // When the report fails to resolve (bad slug, missing instanceId) or
  // is still loading, ReportAuthProvider hasn't mounted yet so it can't
  // emit the chat-lock signal that App.jsx listens for — the AI-chat
  // tab would otherwise show on the error pane. Fire `locked: true`
  // proactively here so the chat hides on any /raportti/<no-page>
  // state. Cleanup emits `locked: false` so the chat comes back on
  // navigation away. The valid-report path will overwrite this with
  // its own auth-derived value once ReportAuthProvider mounts.
  const inErrorOrLoading = !!(error || slugError || loading || !instanceId);
  useEffect(() => {
    if (!inErrorOrLoading) return;
    window.dispatchEvent(new CustomEvent('kuutio:report-chat-locked', { detail: { locked: true } }));
    return () => {
      window.dispatchEvent(new CustomEvent('kuutio:report-chat-locked', { detail: { locked: false } }));
    };
  }, [inErrorOrLoading]);

  const [scenarios, setScenarios] = useState([]);
  const [pathways, setPathways] = useState([]);
  const [pivotPoints, setPivotPoints] = useState([]);
  const [lenses, setLenses] = useState([]);
  const [lensItems, setLensItems] = useState([]);
  const [pivotSummaries, setPivotSummaries] = useState([]);
  const [pivotEdits, setPivotEdits] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [solutionVoteCounts, setSolutionVoteCounts] = useState({});
  const [hybridSolutions, setHybridSolutions] = useState([]);
  const [orgActions, setOrgActions] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  // The curated list is the only source that knows who was actually in the room.
  // participant_count is the Osallistujat list (else active PINs), from the backend.
  const [curatedCount, setCuratedCount] = useState(null);
  const [transcripts, setTranscripts] = useState([]);
  const [perspectives, setPerspectives] = useState([]);
  const [reportSectionIds, setReportSectionIds] = useState(null);

  // Initialise chrome from the instance once; honour ?lang= for shareable
  // translated links. Do not keep re-forcing instance language on every
  // render — that blocked the switcher.
  useEffect(() => {
    if (!instanceInfo) return;
    const raw = instanceInfo?.language ?? instanceInfo?.settings?.language;
    if (!raw || String(raw).trim() === '') return;
    if (langFromUrl) {
      setViewLanguage(langFromUrl);
    } else {
      setLanguage(String(raw).trim());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only when instance first resolves
  }, [instanceInfo?.id]);

  // OpenGraph / Twitter meta tags — sourced from overlay if available,
  // falling back to instance metadata. vaikutuskartta image is at
  // scenarios.map.image_url (the editable slot at the top of the
  // Vaikutuskartta section). Caveat: this is JS-injected, so only
  // crawlers that run JS (Twitter/X, modern previews) pick it up.
  // Facebook/LinkedIn/Slack scrapers see the static index.html shell —
  // server-side rendering is a follow-up for full coverage.
  const metaTitle = instanceInfo
    ? `${get('header.title', instanceInfo.title || 'Kuutio-raportti')} — Kuutio · Sitra`
    : null;
  const metaDescription = instanceInfo
    ? get('header.subtitle', t('report.header.subtitle'))
    : null;
  const metaImage = normalizeImageUrl(get('scenarios.map.image_url', ''));
  useReportMeta({
    title: metaTitle,
    description: metaDescription,
    imageUrl: metaImage,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
  });

  const sectionEnabled = (id) => {
    if (!reportSectionIds) return true;
    return reportSectionIds.has(id);
  };

  // Fetch all data in parallel. `silent` skips the loading spinner so wall
  // refreshes don't flash the whole report blank.
  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (!instanceId) {
      if (slug) {
        if (!silent) setLoading(true);
        return;
      }
      setError('selectInstance');
      setLoading(false);
      return;
    }

    try {
      if (!silent) {
        setError(null);
        setLoading(true);
      }

      const [
        instanceData,
        chainData,
        scenariosData,
        pathwaysData,
        pivotData,
        lensesData,
        lensItemsData,
        pivotEditsData,
        solutionsData,
        hybridsData,
        orgActionsData,
        organisationsData,
        transcriptsData,
        perspectivesData,
        pivotSummariesData,
        solutionVotesData,
        curatedData,
      ] = await Promise.all([
        fetchJson(API_ENDPOINTS.INSTANCE(instanceId)),
        // Sections follow the instance's module chain (#686)
        fetchJson(API_ENDPOINTS.INSTANCE_CHAIN(instanceId)).catch(() => null),
        fetchJson(API_ENDPOINTS.SCENARIOS_WITH_VARIABLES(instanceId)),
        fetchJson(`${API_ENDPOINTS.API_ORIGIN()}/api/pathways?kid=${instanceId}`),
        fetchJson(API_ENDPOINTS.RAG_PIVOT_POINTS(instanceId)).catch(() => null),
        fetchJson(API_ENDPOINTS.LENSES(instanceId)).catch(() => null),
        fetchJson(API_ENDPOINTS.LENS_ITEMS(instanceId)).catch(() => null),
        fetchJson(API_ENDPOINTS.PIVOT_POINT_EDITS(instanceId)).catch(() => null),
        fetchJson(API_ENDPOINTS.SOLUTIONS(instanceId)).catch(() => null),
        fetchJson(API_ENDPOINTS.SOLUTION_HYBRIDS(instanceId)).catch(() => null),
        fetchJson(API_ENDPOINTS.ORG_ACTIONS(instanceId)).catch(() => null),
        fetchJson(API_ENDPOINTS.INSTANCE_ORGANISATIONS(instanceId)).catch(() => null),
        fetchJson(API_ENDPOINTS.TRANSCRIPTS(instanceId)).catch(() => null),
        fetchJson(API_ENDPOINTS.PERSPECTIVES(instanceId)).catch(() => null),
        fetchJson(API_ENDPOINTS.LENS_PIVOT_SUMMARIES(instanceId)).catch(() => null),
        fetchJson(API_ENDPOINTS.SOLUTION_VOTES(instanceId)).catch(() => null),
        fetchJson(API_ENDPOINTS.INSTANCE_CURATED_PARTICIPANTS(instanceId)).catch(() => null),
      ]);

      if (!instanceData) {
        setError('notFound');
        setLoading(false);
        return;
      }

      const sections = chainData?.reportSections;
      if (sections?.length) {
        setReportSectionIds(new Set(sections));
      }

      setInstanceInfo(instanceData);
      if (scenariosData) setScenarios(scenariosData);
      if (pathwaysData) setPathways(pathwaysData.pathways || []);
      if (pivotData) setPivotPoints(pivotData.pivotPoints || pivotData || []);
      if (lensesData) setLenses(Array.isArray(lensesData) ? lensesData : lensesData.lenses || []);
      if (lensItemsData) setLensItems(Array.isArray(lensItemsData) ? lensItemsData : lensItemsData.items || []);
      if (pivotEditsData) setPivotEdits(Array.isArray(pivotEditsData) ? pivotEditsData : pivotEditsData.edits || []);
      if (solutionsData) setSolutions(Array.isArray(solutionsData) ? solutionsData : solutionsData.solutions || []);
      if (hybridsData) setHybridSolutions(Array.isArray(hybridsData) ? hybridsData : []);
      if (orgActionsData) setOrgActions(Array.isArray(orgActionsData) ? orgActionsData : []);
      if (organisationsData) setOrganisations(Array.isArray(organisationsData) ? organisationsData : organisationsData.organisations || []);
      // Same precedence ReportParticipants already uses for its own header:
      // a curated list beats participant_count. Without this the hero and the
      // section below it give two different numbers on the same page.
      if (curatedData?.participants?.length) {
        // Count guests, exactly as ReportParticipants' own header does
        // (participantRows.length): the Kuutio team are organisers, listed
        // separately at the end. Counting the whole list would put 34 in the
        // hero above a section headed 27 — the same two-numbers-one-page
        // problem this fix exists to remove.
        const guests = curatedData.participants.filter(
          (p) => !p.role || p.role === 'participant',
        ).length;
        setCuratedCount(guests || curatedData.participants.length);
      }
      if (transcriptsData) setTranscripts(dropFacilitator(Array.isArray(transcriptsData) ? transcriptsData : transcriptsData.segments || []));
      if (perspectivesData) setPerspectives(Array.isArray(perspectivesData) ? perspectivesData : []);
      if (pivotSummariesData?.summaries) setPivotSummaries(pivotSummariesData.summaries);
      if (solutionVotesData?.counts) setSolutionVoteCounts(solutionVotesData.counts);

      setLoading(false);
    } catch (err) {
      console.error('Error fetching report data:', err);
      if (!silent) {
        setError('loadFailed');
        setLoading(false);
      }
    }
  }, [instanceId, slug]);

  useEffect(() => {
    fetchData({ silent: false });
  }, [fetchData]);
  // An unlock (report PIN or Sitra sign-in) changes what the API returns (#1042): read again.
  const wasLocked = useRef(false);
  useEffect(() => { if (wasLocked.current && !viewerLocked) fetchData({ silent: true }); wasLocked.current = viewerLocked; }, [viewerLocked, fetchData]);

  // Missing implementation plans: generated once per load, by a facilitator of this Kuutio (#867).
  const ensurePlansRef = useRef(false);
  useEffect(() => {
    if (!canEdit || !instanceId || loading || ensurePlansRef.current) return;
    ensurePlansRef.current = true;
    const plan = (s) => s.ai_analysis?.implementationPlan;
    if (!solutions.some(s => !plan(s)?.timeline?.length && !plan(s)?.phases?.length)) return;
    fetch(API_ENDPOINTS.SOLUTION_ENSURE_PLANS(instanceId), { method: 'POST', credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(data => {
        if (data.status !== 'generating') return;
        console.log(`[Report] Generating ${data.missing} implementation plans...`);
        setTimeout(() => {
          fetchJson(API_ENDPOINTS.SOLUTIONS(instanceId))
            .then(fresh => { if (fresh) setSolutions(Array.isArray(fresh) ? fresh : fresh.solutions || []); })
            .catch(e => console.error('[Report] Solutions refresh failed:', e.message));
        }, data.missing * 15000 + 5000);
      })
      .catch(e => console.error('[Report] ensure-plans failed:', e.message));
  }, [canEdit, instanceId, loading, solutions]);

  // Wall / live report: refresh when workshop content changes (120s min — 16 endpoints)
  useContentRefresh(
    instanceId,
    ['lens_items', 'solutions', 'pathways', 'pivot_edits'],
    () => fetchData({ silent: true }),
    { minIntervalMs: 120000, enabled: !instanceInfo?.settings?.locked }
  );

  // Calculate stats
  const stats = useMemo(() => {
    const userSubmissions = pathways.filter(p =>
      p.type !== 'scenario_root' && p.type !== 'variable_root' && !p.is_scenario_starter
    );

    const obstacles = lensItems.filter(i => i.type === 'obstacle').length;
    const opportunities = lensItems.filter(i => i.type === 'opportunity').length;

    return {
      // Devices that submitted are not people (shared iPads), so no fallback to them
      participants: curatedCount || instanceInfo?.participant_count || 0,
      submissions: userSubmissions.length,
      scenarios: scenarios.length,
      pivotPoints: pivotPoints.length,
      obstacles,
      opportunities,
      solutions: solutions.length,
    };
  }, [pathways, scenarios, pivotPoints, lensItems, instanceInfo, solutions, curatedCount]);

  // Group transcripts by phase, separate "Ratkaisut" for embedding in solutions
  const transcriptPhases = useMemo(() => {
    const phases = {};
    const solutionSegments = [];
    for (const seg of transcripts) {
      const phase = seg.phase || seg.phase_name || '';
      if (phase.toLowerCase().includes('ratkaisu')) {
        solutionSegments.push(seg);
      } else {
        if (!phases[phase]) phases[phase] = [];
        phases[phase].push(seg);
      }
    }
    return { phases, solutionSegments };
  }, [transcripts]);

  // Resolve a phase config entry to its segments (if data exists)
  const resolvePhase = (phaseConfig) => {
    const names = [].concat(phaseConfig.phase);
    const matchedKey = Object.keys(transcriptPhases.phases).find(k => names.some(name =>
      k === name || k.toLowerCase().includes(name.toLowerCase().slice(0, 10))));
    if (!matchedKey) return null;
    return { ...phaseConfig, segments: transcriptPhases.phases[matchedKey] || [] };
  };

  // Early transcript phases (before Vaikutuskartta) — renamed per käsikirjoitus
  const earlyPhases = useMemo(() => [
    { phase: 'Tulevaisuuskuvien', sectionId: 'phase-0', label: 'Skenaariot ja tulevaisuuskuvat' },
    { phase: 'Näkökulman rajaus', sectionId: 'phase-1', label: 'Nykytila ja rajaus' },
  ].map(resolvePhase).filter(Boolean), [transcriptPhases.phases]);

  // Late transcript phases — now grouped under single "Mitä jos?" section in nav
  const latePhases = useMemo(() => [
    { phase: ['Mitä jos', 'Entä jos' /* name before #1184 */], sectionId: 'phase-entajos', label: 'Mitä jos -kysymykset' },
    { phase: 'Hiljaiset signaalit', sectionId: 'phase-signaalit', label: 'Hiljaiset signaalit' },
  ].map(resolvePhase).filter(Boolean), [transcriptPhases.phases]);

  // Find the chosen perspective for "Nykytila ja rajaus" section
  const chosenPerspective = useMemo(() =>
    perspectives.find(p => p.is_selected) || null,
    [perspectives]);

  // Per-section visibility from instance settings.reportSections.
  // Defaults: most sections visible; org-actions + first-steps hidden by default
  // (käsikirjoitus: "Piilotetaan tässä vaiheessa Toimenpiteet ja Mitä tehdään ensiksi").
  // Admin UI writes to settings.reportSectionAccess (tri-state) — the
  // resolver below also honours the legacy settings.reportSections
  // boolean for instances that haven't been touched yet.
  const accessOf = (id, defaultVisible = true) =>
    resolveSectionAccess(instanceInfo?.settings, id, defaultVisible);
  // showSection now also treats "authed-but-viewer-locked-out" as hidden,
  // so the nav skips sections the viewer can't see anyway. SectionGate
  // applies the same rule when rendering the section body. Curator's intent
  // is to hide *what the viewer is missing* — a nav entry for a gated
  // section would advertise its existence.
  const showSection = (id, defaultVisible = true) => {
    const a = accessOf(id, defaultVisible);
    if (a === ACCESS_HIDDEN) return false;
    if (a === ACCESS_AUTHED && viewerLocked) return false;
    return true;
  };
  const showReportSection = (id, defaultVisible = true) =>
    sectionEnabled(id) && showSection(id, defaultVisible);

  // Newer modules (pain points, systemic map v2): their own data and sections
  const { data: chainOutputs, available: chainAvailable } = useChainReportData(instanceId, sectionEnabled);
  const chainNav = (ids) => ids
    .filter((id) => chainAvailable.has(id) && showReportSection(id))
    .map((id) => ({ id, label: t(`report.nav.${CHAIN_NAV_KEYS[id]}`) }));

  // Foresight checklist modules (#630): brief, drivers, generated scenarios, preferred future, path, assumptions
  const { data: foresightData, available: foresightAvailable } = useForesightReportData(instanceId, sectionEnabled);
  const { data: futureData, available: futureAvailable } = useFutureReportData(instanceId, sectionEnabled); // Kuviteltu tulevaisuus (#946)
  const foresightNav = (ids) => ids
    .filter((id) => foresightAvailable.has(id) && showReportSection(id))
    .map((id) => ({ id, label: t(`report.nav.${FORESIGHT_NAV_KEYS[id]}`) }));
  // The impact-map view of the scenarios says nothing new when the generated
  // scenarios have their own section and no map was built on them.
  const showImpactScenarios = scenarios.length > 0
    && !(foresightAvailable.has('foresight-scenarios') && !pathways.length);

  // Build nav sections as {id, label} objects in display order (käsikirjoitus 2026-05).
  // Order: Tiivistelmä → Toivottu tulevaisuus 🆕 → Skenaariot ja tulevaisuuskuvat →
  // Nykytila ja rajaus → Vaikutuskartta → Vipupisteet → Esteet ja mahdollisuudet →
  // Mitä jos? 🆕 → Ratkaisut → Osallistujat 🆕 → Tietopohja 🆕.
  // 🆕 sections placeholder-only here — built in V3.* tasks.
  const navSections = useMemo(() => {
    const sections = [];
    if (showReportSection('stats')) sections.push({ id: 'stats', label: t('report.nav.stats') });
    if (showReportSection('future-story')) sections.push({ id: 'future-story', label: t('report.nav.futureStory') });
    earlyPhases.forEach(p => {
      if (showReportSection(p.sectionId)) sections.push({ id: p.sectionId, label: p.label });
    });
    sections.push(...foresightNav(['foresight-brief', 'change-drivers', 'foresight-scenarios', 'preferred-future', 'backcasting-path', 'stress-test']));
    if (showImpactScenarios && showReportSection('scenarios')) sections.push({ id: 'scenarios', label: t('report.nav.scenarios') });
    if (pivotPoints.length > 0 && showReportSection('pivotpoints')) sections.push({ id: 'pivotpoints', label: t('report.nav.pivotpoints') });
    if (lenses.length > 0 && lensItems.length > 0 && showReportSection('lenses')) sections.push({ id: 'lenses', label: t('report.nav.lenses') });
    sections.push(...chainNav(['pain-points', 'ongelmat', 'ankkurit', 'juurisyyt', 'leverage-points', 'vipupisteet-v2', 'systemic-analysis']));
    if (latePhases.length > 0 && (showReportSection('mitajos') || latePhases.some((p) => sectionEnabled(p.sectionId)))) {
      sections.push({ id: 'mitajos', label: t('report.nav.mitajos') });
    }
    if (solutions.length > 0 && showReportSection('solutions')) sections.push({ id: 'solutions', label: t('report.nav.solutions') });
    sections.push(...chainNav(['solution-projections', 'solutions-v4', 'future-interplay-v2', 'future-timeline-v2']));
    sections.push(...FUTURE_SECTIONS.filter((id) => futureAvailable.has(id) && showReportSection(id)).map((id) => ({ id, label: t(`report.nav.${FUTURE_NAV_KEYS[id]}`) })));
    sections.push(...foresightNav(['foresight-assumptions']));
    // Yhdistelmäratkaisut käsikirjoituksen mukaan Ratkaisut-osion alla, ei omaa nav-kohtaa.
    if (showReportSection('participants')) sections.push({ id: 'participants', label: t('report.nav.participants') });
    if (showReportSection('sources')) sections.push({ id: 'sources', label: t('report.nav.sources') });
    // Org actions + first steps hidden by default per käsikirjoitus
    if (orgActions.length > 0 && showReportSection('org-actions', false)) sections.push({ id: 'org-actions', label: t('report.nav.orgActions') });
    if (solutions.length > 0 && showReportSection('first-steps', false) && instanceInfo?.settings?.showImplementationPlans !== false) {
      sections.push({ id: 'first-steps', label: t('report.nav.firstSteps') });
    }
    return sections;
  }, [scenarios, pivotPoints, lenses, lensItems, solutions, hybridSolutions, orgActions, earlyPhases, latePhases, instanceInfo, reportSectionIds, viewerLocked, language, chainAvailable, foresightAvailable, futureAvailable, pathways]);

  if (loading) {
    return (
      <LoadingContainer>
        <Spinner />
        <LoadingText>{t('report.loading')}</LoadingText>
      </LoadingContainer>
    );
  }

  if (error || slugError) {
    return (
      <div style={{ minHeight: '100vh', background: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 15 }}>{slugError ? t('report.loadErrors.slugNotFound', slugError) : t(`report.loadErrors.${error}`)}</p>
      </div>
    );
  }

  const solutionCount = solutions.length + hybridSolutions.length;
  const handleRequestPrint = () => {
    if (solutionCount > 0) setShowPrintModal(true);
    else window.print();
  };
  const confirmPrint = () => {
    setShowPrintModal(false);
    setTimeout(() => window.print(), 100);
  };

  return (
    <ReportAuthProvider
      instanceId={instanceId}
      pinRequired={pinRequired}
    >
    <>
    <EditorDrawerTab />
    <Container>
      <ReportTopBar
        instanceTitle={instanceInfo?.title || ''}
        onPrint={handleRequestPrint}
      />
      {showPrintModal && (
        <ModalWrapper visible={showPrintModal} onClose={() => setShowPrintModal(false)} showCloseButton={false} opacity={0.5} blur={0}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: '24px',
            width: '100%', maxWidth: 400, boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
            position: 'relative',
          }}>
            <button onClick={() => setShowPrintModal(false)} aria-label="Sulje" style={{
              position: 'absolute', top: 10, right: 10, background: 'transparent',
              border: 'none', color: '#6b7280', cursor: 'pointer', padding: 4,
            }}><X size={18} /></button>
            <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem' }}>Tulostusasetukset</h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.95rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={printIncludeSolutions}
                onChange={e => setPrintIncludeSolutions(e.target.checked)}
              />
              Sisällytä ratkaisut kokonaisuudessaan ({solutionCount} kpl)
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button onClick={() => setShowPrintModal(false)} style={{
                padding: '8px 16px', background: '#f5f5f5', color: '#111',
                border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600,
              }}>Peruuta</button>
              <button onClick={confirmPrint} style={{
                padding: '8px 16px', background: '#111', color: '#fff',
                border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
                <Printer size={14} /> {t("report.topbar.print")}
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}
      <ReportNav sections={navSections} />

      <ReportHeader
        instanceInfo={instanceInfo}
        participantCount={stats.participants}
        submissionCount={stats.submissions}
        solutionCount={solutions.length + hybridSolutions.length}
        organisations={organisations}
        onPrintOptionsChange={opts => setPrintIncludeSolutions(opts.includeSolutions)}
      />

      {!isPrintMode && (
        <>
          <div style={{
            maxWidth: 800, margin: '0 auto', padding: '32px 24px 4px',
            fontSize: '0.9rem', color: '#6b7280', lineHeight: 1.6,
            display: 'flex', alignItems: 'flex-start', gap: 16,
          }}>
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: 12, flexShrink: 0, marginTop: 2,
            }}>
              <Sparkles size={20} style={{ color: '#7c3aed' }} />
              <img
                src="/assets/eu/ai-label-basic-black.svg"
                alt={t('report.header.aiLabelAlt')}
                width={40}
                height={40}
              />
            </div>
            <div style={{ borderLeft: '2px solid #e5e7eb', paddingLeft: 16 }}>
              <EditableText
                block
                markdown
                fieldKey="intro.disclaimer"
                fallback={t('report.intro.disclaimer')}
              />
              {isTranslatedView && (
                <div style={{
                  marginTop: 12,
                  padding: '10px 12px',
                  background: 'rgba(124, 58, 237, 0.06)',
                  borderRadius: 8,
                  border: '1px solid rgba(124, 58, 237, 0.15)',
                  color: '#4c1d95',
                  fontSize: '0.88rem',
                }}>
                  <EditableText
                    block
                    fieldKey="intro.translation_disclaimer"
                    fallback={t('report.intro.translationDisclaimer')}
                    vars={{
                      sourceLanguage: t(`report.languageNames.${instanceLanguage}`)
                        || instanceLanguage,
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          {/* Käsikirjoitus 2026-05: yleiskuvaus Kuutiosta + tämän Kuution konteksti */}
          <div style={{
            maxWidth: 800, margin: '8px auto 0', padding: '0 24px',
            fontSize: '0.95rem', color: '#374151', lineHeight: 1.7,
          }}>
            <div style={{ margin: '0 0 12px' }}>
              <EditableText
                block
                markdown
                fieldKey="intro.about_report"
                fallback={t('report.intro.aboutReport')}
              />
            </div>
            <div style={{ margin: '0 0 12px' }}>
              <EditableText
                block
                markdown
                fieldKey="intro.about_method"
                fallback={t('report.intro.aboutMethod')}
              />
            </div>
            <div style={{ margin: 0 }}>
              <EditableText
                block
                markdown
                fieldKey="intro.about_this_kuutio"
                fallback={t('report.intro.aboutThisKuutio')}
              />
            </div>
          </div>
        </>
      )}

      <Main>
        {sectionEnabled('stats') && (
        <SectionGate access={accessOf('stats')} gateTitle={t("report.nav.stats")}>
          <ReportStats stats={stats} id="stats" title={t('report.stats.title')} />
          {/* Käsikirjoitus 2026-05: 2-3 tunnelmakuvaa Kuutiosta. Full-image
              preview (object-fit: contain) + click-to-lightbox via
              AtmosphereGroup — same pattern as TulevaisuuskuvatGroup. */}
          <AtmosphereGroup />
        </SectionGate>
        )}

        {/* Highlight quotes from workshop */}
        {(() => {
          // Extract first 1-2 complete sentences
          const extractQuote = (text) => {
            if (!text) return null;
            const sentences = text.match(/[^.!?]+[.!?]+/g);
            if (!sentences) return null;
            const quote = sentences.slice(0, 2).join(' ').trim();
            return quote.length > 30 && quote.length < 300 ? quote : sentences[0]?.trim() || null;
          };

          const highlights = [];
          // Best transcript quote — first full sentence from a non-facilitator
          // Skip quotes with contextual references that don't stand alone
          const hasContextDependency = (text) => /\b(viitonen|nelonen|kolmonen|kakkonen|ykkönen|tuo\b|noista|siitä syystä|tässä)\b/i.test(text);

          // Score relevance to workshop topic
          const topicWords = (instanceInfo?.title + ' ' + (instanceInfo?.description || '')).toLowerCase()
            .split(/\s+/).filter(w => w.length > 4);
          const topicRelevance = (text) => {
            const lower = text.toLowerCase();
            return topicWords.filter(w => lower.includes(w)).length;
          };

          const transcriptQuotes = transcripts
            .filter(t => t.text?.length > 60)
            .map(t => ({ ...t, sentence: extractQuote(t.text) }))
            .filter(t => t.sentence && t.sentence.length > 40 && t.sentence.length < 250 && !hasContextDependency(t.sentence));
          if (transcriptQuotes.length > 0) {
            const best = transcriptQuotes.sort((a, b) => topicRelevance(b.sentence) - topicRelevance(a.sentence) || b.sentence.length - a.sentence.length)[0];
            // Select on original text (stable winners), display via translation overlay.
            const displayText = extractQuote(
              getTranslated(`transcript.${best.id}.text`, best.text),
            ) || best.sentence;
            highlights.push({ kind: 'transcript', text: displayText, source: t('report.highlights.participant'), phase: best.phase || '', color: '#367ABA' });
          }
          // Impact path — pick a node with a strong title
          const nodeQuotes = pathways
            .filter(p => !p.is_scenario_starter && (p.title || p.text))
            .map(p => ({ ...p, sentence: p.title || p.text }))
            .filter(p => p.sentence.length > 30 && p.sentence.length < 200);
          if (nodeQuotes.length > 0) {
            const best = nodeQuotes.sort((a, b) => topicRelevance(b.sentence) - topicRelevance(a.sentence) || b.sentence.length - a.sentence.length)[0];
            const raw = best.title || best.text;
            const displayText = getTranslated(`quote.${best.id}.text`, raw);
            const phaseTitle = scenarios.find(s => s.id === best.scenarioId)?.title || '';
            const phase = best.scenarioId
              ? getTranslated(`scenario.${best.scenarioId}.title`, phaseTitle)
              : phaseTitle;
            highlights.push({ kind: 'node', text: displayText, source: t('report.highlights.idea'), phase, color: '#00613d' });
          }
          // Top obstacle — full title
          const obstacles = lensItems.filter(i => i.type === 'obstacle' && i.title?.length > 30 && i.title?.length < 200);
          if (obstacles.length > 0) {
            const best = obstacles.sort((a, b) => topicRelevance(b.title) - topicRelevance(a.title) || b.title.length - a.title.length)[0];
            const displayText = getTranslated(`lensItem.${best.id}.title`, best.title);
            const lens = lenses.find(l => l.id === best.lens_id);
            highlights.push({ kind: 'obstacle', text: displayText, source: t('report.highlights.obstacle'), phase: lensDisplayName(lens, language), color: '#fa6129' });
          }
          // Top solution — full title
          const solutionQuotes = solutions.filter(s => s.title?.length > 20 && s.title?.length < 200);
          if (solutionQuotes.length > 0) {
            const best = solutionQuotes[0];
            const displayText = getTranslated(`solution.${best.id}.text`, best.title);
            highlights.push({ kind: 'solution', text: displayText, source: t('report.highlights.solution'), phase: '', color: '#7c3aed' });
          }

          if (highlights.length === 0) return null;
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, margin: '24px 0', width: '100%' }}>
              {highlights.slice(0, 4).map((h) => (
                <div key={h.kind} style={{
                  background: '#f5f5f5', borderRadius: 0, padding: '20px 20px 16px',
                  borderTop: `4px solid ${h.color}`, fontSize: '0.9rem', lineHeight: 1.6, color: '#1a1a1a',
                  flex: '1 1 220px', minWidth: 0, maxWidth: '100%',
                }}>
                  <div style={{ fontStyle: 'italic', marginBottom: 12 }}>
                    "<EditableText fieldKey={`highlight.${h.kind}.text`} fallback={h.text} label={`Huomio — ${h.source}`} />"
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af', display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, color: h.color }}>{h.source}</span>
                    {h.phase && <span>· {h.phase}</span>}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Auto-generated findings summary */}
        {(pathways.length > 0 || pivotPoints.length > 0) && (
          <div style={{
            maxWidth: 800, margin: '8px auto 0', padding: '16px 24px',
            fontSize: '0.95rem', color: '#4b5563', lineHeight: 1.7,
          }}>
            {(() => {
              // Paragraph 1: Overview + key pivot points
              const parts = [];
              if (stats.participants) parts.push(t('report.summary.partParticipants', { n: stats.participants }));
              if (stats.scenarios) parts.push(t('report.summary.partScenarios', { n: stats.scenarios }));
              if (stats.submissions) parts.push(t('report.summary.partSubmissions', { n: stats.submissions }));
              const pivotTitles = pivotPoints.slice(0, 3).map(p =>
                getTranslated(`pivot.${p.id}.title`, p.title || p.representative_text),
              );

              let p1 = t('report.summary.overview', { parts: parts.join(', ') });
              if (pivotTitles.length > 0) {
                const titlesStr = pivotTitles.map(ttl => `"${ttl}"`).join(', ');
                const more = pivotPoints.length > 3 ? t('report.summary.andMore', { n: pivotPoints.length - 3 }) : '';
                p1 += t('report.summary.pivotsIdentified', { titles: titlesStr, more });
              }
              if (solutions.length > 0) {
                p1 += solutions.length === 1
                ? t('report.summary.solutionsOne')
                : t('report.summary.solutionsMany', { n: solutions.length });
              }

              // Paragraph 2: Insights from data
              const insights = [];

              // Most discussed scenario — count by id so selection is language-stable
              const scenarioCounts = {};
              pathways.filter(p => !p.is_scenario_starter && p.scenarioId).forEach(p => {
                scenarioCounts[p.scenarioId] = (scenarioCounts[p.scenarioId] || 0) + 1;
              });
              const topScenarioEntry = Object.entries(scenarioCounts).sort((a, b) => b[1] - a[1])[0];
              if (topScenarioEntry) {
                const [scenarioId, n] = topScenarioEntry;
                const rawTitle = scenarios.find(s => s.id === scenarioId)?.title || scenarioId;
                const scenarioTitle = getTranslated(`scenario.${scenarioId}.title`, rawTitle);
                insights.push(t('report.summary.insightTopScenario', { scenario: scenarioTitle, n }));
              }

              // Obstacle vs opportunity balance
              if (stats.obstacles && stats.opportunities) {
                const ratio = stats.opportunities / (stats.obstacles + stats.opportunities);
                if (ratio > 0.55) insights.push(t('report.summary.insightMoreOpportunities'));
                else if (ratio < 0.45) insights.push(t('report.summary.insightMoreObstacles'));
                else insights.push(t('report.summary.insightBalanced'));
              }

              // Deepest consequence chain
              const maxDepth = Math.max(...pathways.map(p => p.depth || 0), 0);
              if (maxDepth >= 4) {
                insights.push(t('report.summary.insightDeepChain', { n: maxDepth }));
              }

              // Severity vs optimism from pivot points
              const severities = pivotPoints.map(p => p.challengeSeverity || p.metadata?.challengeSeverity).filter(Boolean);
              const optimisms = pivotPoints.map(p => p.solutionOptimism || p.metadata?.solutionOptimism).filter(Boolean);
              if (severities.length > 0 && optimisms.length > 0) {
                const avgSev = severities.reduce((s, v) => s + v, 0) / severities.length;
                const avgOpt = optimisms.reduce((s, v) => s + v, 0) / optimisms.length;
                if (avgSev > 3.5 && avgOpt > 3) insights.push(t('report.summary.insightSevereSolvable'));
                else if (avgSev > 3.5 && avgOpt <= 3) insights.push(t('report.summary.insightSevereCautious'));
                else if (avgOpt > 3.5) insights.push(t('report.summary.insightOptimistic'));
              }

              const p2 = insights.length > 0
                ? insights[0].charAt(0).toUpperCase() + insights[0].slice(1) +
                  (insights.length > 1 ? ', ' + insights.slice(1).join(' ' + t('report.summary.and') + ' ') : '') + '.'
                : '';

              return (
                <>
                  <p style={{ margin: '0 0 8px' }}>
                    <EditableText fieldKey="findings.overview" fallback={p1} />
                  </p>
                  {p2 && (
                    <p style={{ margin: 0 }}>
                      <EditableText fieldKey="findings.insights" fallback={p2} />
                    </p>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* AI Executive Summary */}
        {instanceInfo?.metadata?.report_summary && (() => {
          const s = instanceInfo.metadata.report_summary;
          return (
            <div style={{
              maxWidth: 800, margin: '24px auto 0', padding: '0 24px',
            }}>
              {s.key_findings && (
                <div style={{ marginBottom: 20 }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#000', margin: '0 0 8px' }}>
                    {t('report.executive.keyFindings')}
                  </h3>
                  <p style={{ fontSize: '0.95rem', color: '#4b5563', lineHeight: 1.7, margin: 0 }}>
                    <EditableText fieldKey="exec.key_findings" fallback={s.key_findings} />
                  </p>
                </div>
              )}
              {s.what_we_learned && (
                <div style={{ marginBottom: 20 }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#000', margin: '0 0 8px' }}>
                    {t('report.executive.whatWeLearned')}
                  </h3>
                  <p style={{ fontSize: '0.95rem', color: '#4b5563', lineHeight: 1.7, margin: 0 }}>
                    <EditableText fieldKey="exec.what_we_learned" fallback={s.what_we_learned} />
                  </p>
                </div>
              )}
              {s.what_blocks_progress && (
                <div style={{ marginBottom: 20 }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#000', margin: '0 0 8px' }}>
                    {t('report.executive.whatBlocks')}
                  </h3>
                  <p style={{ fontSize: '0.95rem', color: '#4b5563', lineHeight: 1.7, margin: 0 }}>
                    <EditableText fieldKey="exec.what_blocks_progress" fallback={s.what_blocks_progress} />
                  </p>
                </div>
              )}
              {s.next_steps && (
                <div style={{ marginBottom: 8 }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#000', margin: '0 0 8px' }}>
                    {t('report.executive.nextSteps')}
                  </h3>
                  <p style={{ fontSize: '0.95rem', color: '#4b5563', lineHeight: 1.7, margin: 0 }}>
                    <EditableText fieldKey="exec.next_steps" fallback={s.next_steps} />
                  </p>
                </div>
              )}
              {instanceInfo?.metadata?.report_summary_excluded?.total > 0 && (
                <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '8px 0 0' }}>
                  {t('report.excludedNote', { count: instanceInfo.metadata.report_summary_excluded.total })}
                </p>
              )}
            </div>
          );
        })()}

        <Divider />

        {sectionEnabled('future-story') && (
        <SectionGate access={accessOf('future-story')} gateTitle={t("report.nav.futureStory")}>
          <ReportFutureStory />
          <Divider />
        </SectionGate>
        )}

        {/* Phase 1: Skenaariot ja tulevaisuuskuvat — which future each group chose */}
        {sectionEnabled('phase-0') && (scenarios.some(s => s.selected_tulevaisuuskuva) || earlyPhases.find(p => p.sectionId === 'phase-0')) && (
          <SectionGate access={accessOf('phase-0')} gateTitle={t("report.nav.phase0")}>
          <>
            <Section id="phase-0">
              <SectionTitle>{t('report.phases.scenariosTitle')}</SectionTitle>
              <WorkshopAction>
                <EditableText
                  fieldKey="phase.0.intro"
                  fallback={t('report.phases.scenariosIntro')}
                />
              </WorkshopAction>
              {scenarios.filter(s => s.selected_tulevaisuuskuva).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, margin: '16px 0', width: '100%' }}>
                  {scenarios.filter(s => s.selected_tulevaisuuskuva).map(s => (
                    <div key={s.id} style={{
                      padding: '14px 18px', borderRadius: 0, background: '#f5f5f5',
                      borderLeft: `4px solid ${s.color || '#6b7280'}`,
                      flex: '1 1 220px', minWidth: 0, maxWidth: '100%',
                    }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: s.color || '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                        <EditableText fieldKey={`scenario.${s.id}.title`} fallback={s.title} multiline={false} label="Skenaarion otsikko" />
                      </div>
                      <div style={{ fontSize: '0.95rem', fontWeight: 500, color: '#000' }}>
                        "<EditableText fieldKey={`scenario.${s.id}.selected_tulevaisuuskuva`} fallback={s.selected_tulevaisuuskuva} label="Valittu tulevaisuuskuva" />"
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Käsikirjoitus 2026-05: 4 koonti-slidea, yksi per skenaario.
                  Same in every report — URLs come from Sitra Shared Drive
                  via `apps/backend/src/scripts/populate-tulevaisuuskuvat.js`.
                  Clicking any image opens ImageLightbox with chevron-nav.
                  Curator's order (2026-06): images + per-variant descriptions
                  first so the reader sees the four futures and what each
                  means before reading what each small group locked in. */}
              <TulevaisuuskuvatGroup scenarios={scenarios} />
              {earlyPhases.find(p => p.sectionId === 'phase-0')?.segments?.length > 0 && (
                <ScenarioChoiceCards
                  segments={earlyPhases.find(p => p.sectionId === 'phase-0').segments}
                  scenarios={scenarios}
                />
              )}
            </Section>
            <Divider />
          </>
          </SectionGate>
        )}

        {/* Phase 2: Nykytila ja rajaus — chosen focus perspective */}
        {sectionEnabled('phase-1') && (chosenPerspective || earlyPhases.find(p => p.sectionId === 'phase-1')) && (
          <SectionGate access={accessOf('phase-1')} gateTitle={t("report.nav.phase1")}>
            <Section id="phase-1">
              <SectionTitle>{t('report.phases.perspectiveTitle')}</SectionTitle>
              <WorkshopAction>
                <EditableText
                  fieldKey="phase.1.intro"
                  fallback="Osallistujat äänestivät yhteisen näkökulman, jonka kautta tulevaisuuskuvia tarkastellaan vaikutuspoluissa."
                />
              </WorkshopAction>
              {earlyPhases.find(p => p.sectionId === 'phase-1')?.segments?.length > 0 ? (
                <ReportTranscript
                  segments={earlyPhases.find(p => p.sectionId === 'phase-1').segments}
                  title=""
                  sectionId="phase-1-transcript"
                  chosenPerspective={chosenPerspective}
                  allPerspectives={perspectives}
                />
              ) : chosenPerspective && (
                <div style={{
                  padding: '18px 24px', borderRadius: 0, background: '#f5f5f5',
                  borderLeft: '6px solid #000', margin: '16px 0',
                }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#367ABA', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                    {t('report.executive.selectedPerspective')}
                  </div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 600, color: '#000', lineHeight: 1.5 }}>
                    <EditableText
                      fieldKey={`perspective.${chosenPerspective.id}.title`}
                      fallback={chosenPerspective.custom_title || chosenPerspective.title}
                      multiline={false}
                      label={t('report.executive.selectedPerspective')}
                    />
                  </div>
                </div>
              )}
            </Section>
            <Divider />
          </SectionGate>
        )}

        <ReportForesightSections ids={['foresight-brief', 'change-drivers', 'foresight-scenarios', 'preferred-future', 'backcasting-path', 'stress-test']}
          data={foresightData} available={foresightAvailable} show={showReportSection} accessOf={accessOf} />

        {sectionEnabled('scenarios') && showImpactScenarios && (
          <SectionGate access={accessOf('scenarios')} gateTitle={t("report.nav.scenarios")}>
            <ReportScenarios scenarios={scenarios} pathways={pathways} />
            <Divider />
          </SectionGate>
        )}

        {sectionEnabled('pivotpoints') && pivotPoints.length > 0 && (
          <SectionGate access={accessOf('pivotpoints')} gateTitle={t("report.nav.pivotpoints")}>
            <ReportPivotPoints
              pivotPoints={pivotPoints}
              pivotEdits={pivotEdits}
              lensItems={lensItems}
              pivotSummaries={pivotSummaries}
              selectedPivotIds={instanceInfo?.metadata?.selectedPivotIds || []}
              scenarios={scenarios}
            />
            <Divider />
          </SectionGate>
        )}

        {/* "Mitä jos?" — käsikirjoitus 2026-05. Yhdistää aiemman Entä jos -
            kysymykset + Hiljaiset signaalit -osiot. ReportWhatIf renderöi
            10 kysymyspaikkaa (V3.2), osallistujien transkriptit children-propsina. */}
        {(sectionEnabled('mitajos') || latePhases.some((p) => sectionEnabled(p.sectionId))) && (
        <SectionGate access={accessOf('mitajos')} gateTitle={t("report.nav.mitajos")}>
            <ReportWhatIf questions={instanceInfo?.metadata?.hmw_questions}>
              {latePhases.filter((phase) => sectionEnabled(phase.sectionId) || sectionEnabled('mitajos')).map(phase => {
                // Mitä jos? → cards only. ParticipantWhatIfCards replaces
                // the chat-bubble + minimised history view because it
                // surfaces the same content (the 9 participant questions)
                // in a denser, scannable shape.
                if (phase.sectionId === 'phase-entajos') {
                  return <ParticipantWhatIfCards key={phase.sectionId} segments={phase.segments} />;
                }
                // Other late phases (Hiljaiset signaalit) keep the chat
                // transcript view until they get their own card treatment.
                const fallbackDescription = 'Osallistujat jakoivat hiljaisia signaaleja, villikortteja ja esimerkkejä maailmalta.';
                return (
                  <ReportTranscript
                    key={phase.sectionId}
                    sectionId={phase.sectionId}
                    title={phase.label}
                    description={
                      <EditableText
                        fieldKey={`phase.${phase.sectionId}.description`}
                        fallback={fallbackDescription}
                      />
                    }
                    segments={phase.segments}
                  />
                );
              })}
            </ReportWhatIf>
            <Divider />
        </SectionGate>
        )}

        {sectionEnabled('lenses') && lenses.length > 0 && lensItems.length > 0 && (
          <SectionGate access={accessOf('lenses')} gateTitle={t("report.nav.lenses")}>
            <ReportLenses
              lenses={lenses}
              lensItems={lensItems}
              pivotPoints={pivotPoints}
              pivotEdits={pivotEdits}
              printMode={isPrintMode}
              summaryText={(() => {
                const obstacles = lensItems.filter(i => i.type === 'obstacle');
                const opportunities = lensItems.filter(i => i.type === 'opportunity');
                const topObstacleLens = lenses.reduce((best, l) => {
                  const count = obstacles.filter(i => i.lens_id === l.id).length;
                  return count > (best.count || 0) ? { name: lensDisplayName(l, language), count } : best;
                }, {});
                const topOppLens = lenses.reduce((best, l) => {
                  const count = opportunities.filter(i => i.lens_id === l.id).length;
                  return count > (best.count || 0) ? { name: lensDisplayName(l, language), count } : best;
                }, {});
                let fallback = t('report.lenses.summaryIdentified', {
                  obstacleCount: obstacles.length,
                  opportunityCount: opportunities.length,
                  topObstacleLens: topObstacleLens.name || '',
                  topObstacleCount: topObstacleLens.count || 0,
                  topOppLens: topOppLens.name || '',
                  topOppCount: topOppLens.count || 0,
                });
                if (opportunities.length > obstacles.length) {
                  fallback += t('report.lenses.summaryMoreOpportunities');
                } else if (obstacles.length > opportunities.length) {
                  fallback += t('report.lenses.summaryMoreObstacles');
                } else {
                  fallback += t('report.lenses.summaryBalanced');
                }
                return <EditableText fieldKey="lenses.summary" fallback={fallback} />;
              })()}
            />
            <Divider />
          </SectionGate>
        )}

        <ReportChainSections ids={['pain-points', 'ongelmat', 'ankkurit', 'juurisyyt', 'leverage-points', 'vipupisteet-v2', 'systemic-analysis']}
          data={chainOutputs} available={chainAvailable} show={showReportSection} accessOf={accessOf} />

        {sectionEnabled('solutions') && solutions.length > 0 && (
          <SectionGate access={accessOf('solutions')} gateTitle={t("report.nav.solutions")}>
            <ReportSolutions
              solutions={solutions}
              voteCounts={solutionVoteCounts}
              showImplementationPlans={showSection('implementation-plans', true) && instanceInfo?.settings?.showImplementationPlans !== false}
              summaryText={(() => {
                const groups = new Set(solutions.map(s => s.group_name));
                const analyzed = solutions.filter(s => s.ai_analysis?.evaluation);
                const scores = analyzed.map(s => getCompositeScore(s.ai_analysis)).filter((x) => x !== null && x !== undefined);
                const avgScore = scores.length > 0 ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1) : null;
                const tagAnalyzed = analyzed.filter(s => isTagBasedEvaluation(s.ai_analysis?.evaluation));
                let text = t('report.solutions.summaryGroups', {
                  groupCount: groups.size,
                  solutionCount: solutions.length,
                });
                if (avgScore) text += t('report.solutions.summaryAvgScore', { avgScore });
                if (tagAnalyzed.length) text += t('report.solutions.summaryTagBased');
                return <EditableText fieldKey="solutions.summary" fallback={text} />;
              })()}
            />
          </SectionGate>
        )}

        <ReportChainSections ids={['solution-projections', 'solutions-v4', 'future-interplay-v2', 'future-timeline-v2']}
          data={chainOutputs} available={chainAvailable} show={showReportSection} accessOf={accessOf} />
        <ReportFutureSections ids={FUTURE_SECTIONS} data={futureData} available={futureAvailable} show={showReportSection} accessOf={accessOf} />

        <ReportForesightSections ids={['foresight-assumptions']}
          data={foresightData} available={foresightAvailable} show={showReportSection} accessOf={accessOf} />

        {/* Yhdistelmäratkaisut visible iff Ratkaisut visible — käsikirjoitus 2026-05 */}
        {sectionEnabled('hybrid-solutions') && hybridSolutions.length > 0 && (
          <SectionGate access={accessOf('solutions')} gateTitle={t("report.nav.hybrids")}>
            <ReportHybridSolutions
              hybrids={hybridSolutions}
              solutions={solutions}
              showImplementationPlans={showSection('implementation-plans', true) && instanceInfo?.settings?.showImplementationPlans !== false}
            />
          </SectionGate>
        )}

        {sectionEnabled('participants') && (
        <SectionGate access={accessOf('participants')} gateTitle={t("report.nav.participants")}>
          <>
            <Divider />
            <ReportParticipants instanceId={instanceId} count={stats.participants} />
          </>
        </SectionGate>
        )}

        {sectionEnabled('sources') && (
        <SectionGate access={accessOf('sources')} gateTitle={t("report.nav.sources")}>
          <Divider />
          <ReportSources instanceId={instanceId} instanceTopic={instanceInfo?.title} />
        </SectionGate>
        )}

        {sectionEnabled('org-actions') && orgActions.length > 0 && (
          <SectionGate access={accessOf('org-actions', false)} gateTitle={t("report.nav.orgActions")}>
            <ReportOrgActions
              actions={orgActions}
              solutions={[...solutions, ...hybridSolutions]}
              printMode={isPrintMode}
            />
          </SectionGate>
        )}

        {/* Print-only: full solution details when toggled */}
        {printIncludeSolutions && [...solutions, ...hybridSolutions].length > 0 && (
          <PrintOnlySection id="solutions-full">
            <SectionTitle>{t('report.solutions.printFullTitle')}</SectionTitle>
            {[...solutions, ...hybridSolutions]
              .sort((a, b) => (a.order_index ?? 999) - (b.order_index ?? 999))
              .map(sol => (
                <div key={sol.id} style={{ marginBottom: 32, pageBreakInside: 'avoid' }}>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: '#000', margin: '0 0 8px' }}>
                    <EditableText
                      fieldKey={`solution.${sol.id}.title`}
                      fallback={sol.metadata?.ai_title || sol.title?.split(/[.!?\n]/)[0] || sol.title}
                      multiline={false}
                    />
                    {isTagBasedEvaluation(sol.ai_analysis?.evaluation) && sol.ai_analysis.evaluation.tags?.length > 0 && (
                      <span style={{ marginLeft: 12, fontSize: 13, fontWeight: 600, color: '#4b5563' }}>
                        {sol.ai_analysis.evaluation.tags.map(tag => (getEvaluationTagLabels(hasStaticUi(language) ? language : 'en')[tag.id] || tag.id)).join(' · ')}
                      </span>
                    )}
                    {!isTagBasedEvaluation(sol.ai_analysis?.evaluation) && getCompositeScore(sol.ai_analysis) != null && (
                      <span style={{ marginLeft: 12, fontSize: 14, fontWeight: 600, color: '#059669' }}>
                        {getCompositeScore(sol.ai_analysis)} / 5
                      </span>
                    )}
                  </h3>
                  <p style={{ fontSize: 14, lineHeight: 1.7, color: '#444', whiteSpace: 'pre-line' }}>
                    <EditableText
                      fieldKey={`solution.${sol.id}.description`}
                      fallback={sol.description || sol.title || ''}
                    />
                  </p>
                  <SolutionBlockerAnswers
                    solutionId={sol.id}
                    narrative={sol.ai_analysis?.blockerNarrative}
                  />
                  <SolutionAnalysis analysis={sol.ai_analysis} solutionId={sol.id} />
                  {showSection('implementation-plans', true) && instanceInfo?.settings?.showImplementationPlans !== false && (
                    <SolutionImplementationPlan plan={sol.ai_analysis?.implementationPlan} solutionId={sol.id} />
                  )}
                </div>
              ))}
          </PrintOnlySection>
        )}

        {/* Very last section: cross-solution "what each org does first" — hidden by default per käsikirjoitus */}
        {sectionEnabled('first-steps') && solutions.length > 0 && instanceInfo?.settings?.showImplementationPlans !== false && (
          <SectionGate access={accessOf('first-steps', false)} gateTitle={t("report.nav.firstSteps")}>
            <SolutionsFirstSteps
              solutions={solutions}
              organisations={organisations}
              orgActions={orgActions}
            />
          </SectionGate>
        )}
      </Main>

      <Footer>
        <img className="sitra-wordmark-img" src="/assets/sitra_logo_musta.svg" alt="Sitra" />
        <a className="sitra-link" href="https://www.sitra.fi" target="_blank" rel="noreferrer">
          sitra.fi
        </a>
        <span className="meta">
          {t('report.footer.generatedOn', {
            date: instanceInfo?.last_edited_at ? new Date(instanceInfo.last_edited_at).toLocaleDateString(dateLocale(language)) : '',
          })}{' '}
          <img src="/assets/kuutio-icon.svg" alt="" aria-hidden="true" />
          Kuutio Platform
        </span>
      </Footer>
    </Container>
    </>
    </ReportAuthProvider>
  );
}
